import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { ShieldAlert, Search, RefreshCw, Download } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const STATUS_TONE = {
  active: "bg-amber-100 text-amber-800 border-amber-200",
  ended: "bg-emerald-100 text-emerald-800 border-emerald-200",
  expired: "bg-stone-200 text-stone-700 border-stone-300",
};

const STATUS_LABEL = {
  active: "Em andamento",
  ended: "Encerrada",
  expired: "Expirada (2h)",
};

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

function toCsv(rows) {
  const header = [
    "ID",
    "Super Admin",
    "Email Admin",
    "Empresa",
    "Company ID",
    "Inicio",
    "Fim",
    "Duracao (s)",
    "Status",
  ];
  const escape = (v) => {
    const s = String(v ?? "");
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.id,
        r.actor_name,
        r.actor_email,
        r.company_trade_name,
        r.company_id,
        r.started_at,
        r.ended_at || "",
        r.duration_seconds,
        r.status,
      ]
        .map(escape)
        .join(",")
    );
  }
  return lines.join("\n");
}

export default function AdminSupportSessions() {
  const [data, setData] = useState({ summary: null, sessions: [] });
  const [loading, setLoading] = useState(true);
  const [filterCompany, setFilterCompany] = useState("");
  const [filterActor, setFilterActor] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/admin/support-sessions?limit=500");
      setData(resp);
    } catch (err) {
      console.error("Failed to load support sessions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const c = filterCompany.trim().toLowerCase();
    const a = filterActor.trim().toLowerCase();
    return (data.sessions || []).filter((s) => {
      if (
        c &&
        !(s.company_trade_name || "").toLowerCase().includes(c) &&
        !(s.company_id || "").toLowerCase().includes(c)
      ) {
        return false;
      }
      if (
        a &&
        !(s.actor_email || "").toLowerCase().includes(a) &&
        !(s.actor_name || "").toLowerCase().includes(a)
      ) {
        return false;
      }
      return true;
    });
  }, [data.sessions, filterCompany, filterActor]);

  const downloadCsv = () => {
    const csv = toCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chefcheck-support-sessions-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summary = data.summary || {};

  return (
    <Layout
      title="Sessões de Suporte"
      subtitle="Trilha de auditoria de impersonações (Modo Suporte) — LGPD/SOC2"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            className="border-stone-300 bg-white"
            data-testid="support-sessions-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
          </Button>
          <Button
            onClick={downloadCsv}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="support-sessions-export"
            disabled={filtered.length === 0}
          >
            <Download className="h-4 w-4 mr-2" /> Exportar CSV
          </Button>
        </div>
      }
    >
      {/* Summary cards */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4"
        data-testid="support-sessions-summary"
      >
        <Card className="p-4 rounded-xl border-stone-200 bg-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-body">
            Total
          </div>
          <div className="font-display text-2xl font-extrabold mt-1">
            {summary.total ?? "—"}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-amber-200 bg-amber-50">
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-body">
            Em andamento
          </div>
          <div className="font-display text-2xl font-extrabold mt-1 text-amber-900">
            {summary.active ?? "—"}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-emerald-200 bg-emerald-50">
          <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 font-body">
            Encerradas
          </div>
          <div className="font-display text-2xl font-extrabold mt-1 text-emerald-900">
            {summary.ended ?? "—"}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-stone-200 bg-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-body">
            Super-admins
          </div>
          <div className="font-display text-2xl font-extrabold mt-1">
            {summary.distinct_actors ?? "—"}
          </div>
        </Card>
        <Card className="p-4 rounded-xl border-stone-200 bg-white">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-body">
            Empresas acessadas
          </div>
          <div className="font-display text-2xl font-extrabold mt-1">
            {summary.distinct_companies ?? "—"}
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-xl border-stone-200 bg-white mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-body text-stone-600">
              Filtrar por empresa
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
              <Input
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                placeholder="Nome ou ID da empresa..."
                className="pl-9 bg-white"
                data-testid="support-sessions-filter-company"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-body text-stone-600">
              Filtrar por super-admin
            </Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
              <Input
                value={filterActor}
                onChange={(e) => setFilterActor(e.target.value)}
                placeholder="E-mail ou nome..."
                className="pl-9 bg-white"
                data-testid="support-sessions-filter-actor"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Sessions table */}
      <Card
        data-testid="support-sessions-table"
        className="rounded-xl border-stone-200 bg-white overflow-hidden"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Quando</TableHead>
              <TableHead className="font-body">Super Admin</TableHead>
              <TableHead className="font-body">Empresa</TableHead>
              <TableHead className="font-body">Encerrou em</TableHead>
              <TableHead className="font-body">Duração</TableHead>
              <TableHead className="font-body">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-stone-500 py-10 font-body text-sm"
                >
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center text-stone-500 py-10"
                >
                  <ShieldAlert className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Nenhuma sessão de suporte registrada
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((s) => (
                <TableRow
                  key={s.id}
                  data-testid={`support-session-row-${s.id}`}
                >
                  <TableCell className="font-body text-sm">
                    {formatDateTime(s.started_at)}
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    <div className="font-medium text-stone-900">
                      {s.actor_name || "—"}
                    </div>
                    <div className="text-xs text-stone-500">
                      {s.actor_email}
                    </div>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    <div className="font-medium text-stone-900">
                      {s.company_trade_name || "—"}
                    </div>
                    <div className="text-xs text-stone-500">{s.company_id}</div>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    {s.ended_at ? formatDateTime(s.ended_at) : "—"}
                  </TableCell>
                  <TableCell className="font-body text-sm tabular-nums">
                    {formatDuration(s.duration_seconds)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        STATUS_TONE[s.status] || "bg-stone-100 text-stone-700"
                      }
                    >
                      {STATUS_LABEL[s.status] || s.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
